import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/TopTracks.css';

const TopTracks = () => {
  const { handleTrackClick } = useContext(AudioContext);
  const [topTracks, setTopTracks] = useState([]);
  const [userData, setUserData] = useState({
    artistName: '',
    profilePicture: '',
    bio: '',
  });
  const [loading, setLoading] = useState(true);
  const [pinnedTracks, setPinnedTracks] = useState([]);
  const [plan, setPlan] = useState(null);
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const response = await axios.get(
            `https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPlan(response.data.plan);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    };

    fetchUserPlan();
  }, [auth.currentUser]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const firebaseUid = auth.currentUser?.uid;
        if (!firebaseUid) {
          navigate('/login');
          return;
        }

        // Fetch user data
        const userResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/users/firebase/${firebaseUid}`
        );
        setUserData(userResponse.data);

        // Fetch pinned tracks
        const pinnedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/pinned`,
          { params: { firebaseUid } }
        );
        setPinnedTracks(pinnedResponse.data);

        // Fetch uploads
        const uploadsResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads`,
          { params: { firebaseUid } }
        );

        // Sort uploads by plays to get top tracks
        const sortedTracks = uploadsResponse.data.sort((a, b) => (b.plays || 0) - (a.plays || 0));
        setTopTracks(sortedTracks);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error.response ? error.response.data : error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [auth, navigate]);

  const handleDeleteTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (window.confirm('Are you sure you want to delete this track?')) {
      try {
        await axios.delete('https://sync-api-troz.onrender.com/api/uploads/delete-track', {
          data: { trackId, firebaseUid },
        });
        window.alert('Track deleted successfully');
        setTopTracks((prevTracks) => prevTracks.filter((track) => track._id !== trackId));
      } catch (error) {
        console.error('Error deleting track:', error);
        window.alert('Failed to delete track');
      }
    }
  };

  const handleLikeTrack = async (trackId, trackUserId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        { trackId, firebaseUid: currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Track liked successfully!');
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      if (error.response?.data?.message === 'You have already liked this track') {
        window.alert('You have already liked this track.');
      } else {
        window.alert('An error occurred while liking the track.');
      }
    }
  };

  const handlePinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/pin', {
        firebaseUid,
        trackId,
      });
      const pinnedResponse = await axios.get(
        `https://sync-api-troz.onrender.com/api/uploads/pinned`,
        { params: { firebaseUid } }
      );
      setPinnedTracks(pinnedResponse.data);
    } catch (error) {
      console.error('Error pinning track:', error);
    }
  };

  const handleUnpinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/unpin', {
        firebaseUid,
        trackId,
      });
      setPinnedTracks((prev) => prev.filter((track) => track._id !== trackId));
    } catch (error) {
      console.error('Error unpinning track:', error);
    }
  };

  if (loading) {
    return (
      <div className="top-tracks-loading">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="top-tracks-container">
      <div className="profile-banner">
        {userData.profilePicture ? (
          <img src={userData.profilePicture} alt="Banner" className="banner-image" />
        ) : (
          <div className="banner-placeholder" />
        )}
        <p className="artist-name">{userData.artistName}</p>
      </div>
      <div className="scroll-content">
        <div className="header">
          <button onClick={() => navigate(-1)} className="back-button">
            <FaArrowLeft size={24} color="#fff" />
          </button>
          <p className="header-text">Top Tracks</p>
          <div className="placeholder" />
        </div>
        {topTracks.length > 0 ? (
          topTracks.map((track) => (
            <TrackItem
              key={track._id}
              track={track}
              onPlay={() => handleTrackClick(track)}
              onPin={() => handlePinTrack(track._id)}
              onUnpin={() => handleUnpinTrack(track._id)}
              isPinned={pinnedTracks.some((t) => t._id === track._id)}
              onDelete={() => handleDeleteTrack(track._id)}
              onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
              currentUserId={auth.currentUser?.uid}
            />
          ))
        ) : (
          <p className="no-tracks-text">You don't have any tracks yet</p>
        )}
      </div>
    </div>
  );
};

export default TopTracks;